import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const DisputesComponent = ({ user }) => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOpen, setShowOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [evidenceText, setEvidenceText] = useState('');
  const [form, setForm] = useState({ escrow_id: '', reason: '', description: '' });
  const [adminData, setAdminData] = useState(null);
  const [resolveForm, setResolveForm] = useState({ resolution_type: 'split_50_50', resolution_note: '', percentage: 70 });

  const isAdmin = user?.email === 'admin@cualapp.com';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/disputes/my`);
      setDisputes(res.data.disputes || []);
      if (isAdmin) {
        const adminRes = await axios.get(`${API}/disputes/admin/all`);
        setAdminData(adminRes.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openDispute = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await axios.post(`${API}/disputes/open`, form);
      alert('Disputa abierta exitosamente');
      setShowOpen(false);
      setForm({ escrow_id: '', reason: '', description: '' });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const addEvidence = async (disputeId) => {
    if (!evidenceText.trim()) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/disputes/evidence/${disputeId}`, { text: evidenceText });
      alert('Evidencia agregada');
      setEvidenceText('');
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const resolveDispute = async (disputeId) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/disputes/resolve/${disputeId}`, resolveForm);
      alert(res.data.message);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const statusColor = (s) => {
    if (s === 'open') return 'bg-red-100 text-red-700';
    if (s === 'under_review') return 'bg-amber-100 text-amber-700';
    if (s === 'resolved') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-600';
  };

  const resolutionLabels = {
    full_refund: 'Reembolso Completo',
    full_release: 'Liberacion Completa',
    split_50_50: 'Division 50/50',
    partial_refund: 'Reembolso Parcial',
    partial_release: 'Liberacion Parcial'
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando disputas...</div>;

  const displayDisputes = isAdmin && adminData ? adminData.disputes : disputes;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="disputes-module">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mediacion & Disputas</h2>
          <p className="text-sm text-gray-500">Resolucion de conflictos en Escrow</p>
        </div>
        <div className="flex gap-2 items-center">
          {isAdmin && adminData && (
            <div className="flex gap-2 text-xs mr-3">
              <span className="px-2 py-1 bg-red-50 text-red-600 rounded-lg">{adminData.stats.open} abiertas</span>
              <span className="px-2 py-1 bg-green-50 text-green-600 rounded-lg">{adminData.stats.resolved} resueltas</span>
            </div>
          )}
          <button onClick={() => setShowOpen(!showOpen)} data-testid="open-dispute-btn"
            className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-500 transition-colors">
            {showOpen ? 'Cancelar' : 'Abrir Disputa'}
          </button>
        </div>
      </div>

      {/* How disputes work */}
      <div className="bg-gradient-to-r from-red-50 to-amber-50 border border-red-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-red-700 mb-2">Como funciona la mediacion</h3>
        <div className="grid grid-cols-4 gap-2 text-xs text-red-600">
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-exclamation-triangle text-lg mb-1 block"></i>
            <strong>1. Abrir</strong><br/>Indica razon y evidencia
          </div>
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-comment text-lg mb-1 block"></i>
            <strong>2. Evidencia</strong><br/>Ambas partes presentan
          </div>
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-balance-scale text-lg mb-1 block"></i>
            <strong>3. Revision</strong><br/>Admin evalua el caso
          </div>
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-check-circle text-lg mb-1 block"></i>
            <strong>4. Resolucion</strong><br/>Fondos distribuidos
          </div>
        </div>
      </div>

      {/* Open dispute form */}
      {showOpen && (
        <div className="bg-white rounded-xl shadow-lg border border-red-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Abrir Nueva Disputa</h3>
          <form onSubmit={openDispute} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID del Escrow</label>
              <input type="text" required value={form.escrow_id} onChange={e => setForm({...form, escrow_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="ID del escrow en disputa" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Razon</label>
              <select value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Seleccionar razon...</option>
                <option value="producto_no_recibido">Producto no recibido</option>
                <option value="producto_danado">Producto danado o defectuoso</option>
                <option value="no_como_descrito">No es como se describio</option>
                <option value="servicio_incompleto">Servicio incompleto</option>
                <option value="fraude">Sospecha de fraude</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion detallada</label>
              <textarea required value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-24 resize-none"
                placeholder="Describe el problema con la mayor cantidad de detalles posible..." />
            </div>
            <button type="submit" disabled={processing} data-testid="submit-dispute-btn"
              className="w-full bg-red-600 text-white py-2.5 rounded-lg font-medium hover:bg-red-500 disabled:opacity-50">
              {processing ? 'Enviando...' : 'Abrir Disputa'}
            </button>
          </form>
        </div>
      )}

      {/* Disputes list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{isAdmin ? 'Todas las Disputas' : 'Mis Disputas'} ({displayDisputes.length})</h3>
        </div>

        {displayDisputes.length === 0 ? (
          <div className="p-12 text-center">
            <i className="fa fa-balance-scale text-4xl text-gray-300 mb-3 block"></i>
            <p className="text-gray-500">No hay disputas</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {displayDisputes.map(d => (
              <div key={d.id} className="p-5">
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(d.status)}`}>{d.status}</span>
                      <span className="text-xs text-gray-400">#{d.id.slice(0, 8)}</span>
                      {d.my_role && <span className={`text-xs px-1.5 py-0.5 rounded ${d.my_role === 'buyer' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'}`}>{d.my_role}</span>}
                    </div>
                    <p className="text-sm font-medium text-gray-900">{d.reason?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-500">{d.amount} {d.currency} | {new Date(d.created_at).toLocaleString('es-ES')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{d.evidence?.length || 0} evidencia(s)</p>
                    <i className={`fa fa-chevron-${expandedId === d.id ? 'up' : 'down'} text-gray-400 text-xs`}></i>
                  </div>
                </div>

                {expandedId === d.id && (
                  <div className="mt-4 space-y-3" onClick={e => e.stopPropagation()}>
                    {/* Evidence thread */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-gray-600">Hilo de Evidencia:</h4>
                      {d.evidence?.map((ev, i) => (
                        <div key={i} className={`p-3 rounded-lg text-sm ${ev.from === 'buyer' ? 'bg-blue-50 border-l-3 border-blue-400' : 'bg-green-50 border-l-3 border-green-400'}`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium text-gray-600">{ev.from === 'buyer' ? 'Comprador' : 'Vendedor'}</span>
                            <span className="text-xs text-gray-400">{new Date(ev.timestamp).toLocaleString('es-ES')}</span>
                          </div>
                          <p className="text-gray-800">{ev.text}</p>
                        </div>
                      ))}
                    </div>

                    {/* Add evidence */}
                    {d.status !== 'resolved' && (
                      <div className="flex gap-2">
                        <input type="text" value={evidenceText} onChange={e => setEvidenceText(e.target.value)}
                          placeholder="Agregar evidencia o respuesta..." data-testid={`evidence-input-${d.id}`}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                        <button onClick={() => addEvidence(d.id)} disabled={processing} data-testid={`add-evidence-${d.id}`}
                          className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
                          Enviar
                        </button>
                      </div>
                    )}

                    {/* Resolution (shown if resolved) */}
                    {d.status === 'resolved' && d.resolution && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-green-700 mb-1">Resolucion</h4>
                        <p className="text-sm text-green-800">{d.resolution}</p>
                        {d.resolution_note && <p className="text-xs text-green-600 mt-1">Nota: {d.resolution_note}</p>}
                      </div>
                    )}

                    {/* Admin resolve panel */}
                    {isAdmin && d.status !== 'resolved' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-amber-700">Resolver Disputa (Admin)</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Tipo de Resolucion</label>
                            <select value={resolveForm.resolution_type} onChange={e => setResolveForm({...resolveForm, resolution_type: e.target.value})}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
                              {Object.entries(resolutionLabels).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </div>
                          {(resolveForm.resolution_type === 'partial_refund' || resolveForm.resolution_type === 'partial_release') && (
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Porcentaje (%)</label>
                              <input type="number" min="1" max="99" value={resolveForm.percentage}
                                onChange={e => setResolveForm({...resolveForm, percentage: parseInt(e.target.value)})}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Nota del Mediador</label>
                          <input type="text" value={resolveForm.resolution_note}
                            onChange={e => setResolveForm({...resolveForm, resolution_note: e.target.value})}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="Razon de la decision..." />
                        </div>
                        <button onClick={() => resolveDispute(d.id)} disabled={processing} data-testid={`resolve-${d.id}`}
                          className="w-full bg-amber-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-amber-500 disabled:opacity-50">
                          {processing ? 'Resolviendo...' : `Resolver: ${resolutionLabels[resolveForm.resolution_type]}`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputesComponent;
